export function useAccessibility() {
  const generateAriaLabel = (action: string, context?: string) => {
    if (!context)
      return action;
    return `${action} ${context}`;
  };

  const handleKeyDown = (event: KeyboardEvent, callback: () => void) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      callback();
    }
  };

  const handleKeyDownWithEscape = (event: KeyboardEvent, callback: () => void, escapeCallback?: () => void) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      callback();
    } else if (event.key === 'Escape' && escapeCallback) {
      event.preventDefault();
      escapeCallback();
    }
  };

  const getFocusableElements = (container: HTMLElement) => {
    return container.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
  };

  const trapFocus = (container: HTMLElement) => {
    const focusableElements = getFocusableElements(container);
    const firstElement = focusableElements[0] as HTMLElement;
    const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

    const handleTabKey = (event: KeyboardEvent) => {
      if (event.key === 'Tab') {
        if (event.shiftKey) {
          if (document.activeElement === firstElement) {
            event.preventDefault();
            lastElement.focus();
          }
        } else {
          if (document.activeElement === lastElement) {
            event.preventDefault();
            firstElement.focus();
          }
        }
      }
    };

    container.addEventListener('keydown', handleTabKey);

    return () => {
      container.removeEventListener('keydown', handleTabKey);
    };
  };

  const announceToScreenReader = (message: string, priority: 'polite' | 'assertive' = 'polite') => {
    const announcement = document.createElement('div');
    announcement.setAttribute('aria-live', priority);
    announcement.setAttribute('aria-atomic', 'true');
    announcement.className = 'sr-only';
    announcement.textContent = message;

    document.body.appendChild(announcement);

    setTimeout(() => {
      document.body.removeChild(announcement);
    }, 1000);
  };

  return {
    generateAriaLabel,
    handleKeyDown,
    handleKeyDownWithEscape,
    getFocusableElements,
    trapFocus,
    announceToScreenReader,
  };
}
